// SkillsForm Component
// Form for managing player skills

import React, { useState } from 'react';
import { FaPlus, FaTrash, FaStar, FaRegStar } from 'react-icons/fa';
import styles from './SkillsForm.module.css';
import { useNotification } from '../../../context/NotificationContext';

const SKILL_CATEGORIES = {
  technical: {
    label: 'Technical Skills',
    suggestions: [
      'Ball Control', 'Passing Accuracy', 'Shooting', 'Dribbling',
      'First Touch', 'Crossing', 'Free Kicks', 'Heading',
      'Tackling', 'Marking', 'Positioning'
    ]
  },
  tactical: {
    label: 'Tactical Skills',
    suggestions: [
      'Game Intelligence', 'Vision', 'Decision Making', 'Positioning',
      'Team Play', 'Leadership', 'Communication', 'Adaptability',
      'Strategic Thinking', 'Field Awareness'
    ]
  },
  physical: {
    label: 'Physical Skills',
    suggestions: [
      'Speed', 'Agility', 'Strength', 'Endurance', 'Balance',
      'Flexibility', 'Power', 'Coordination', 'Reaction Time',
      'Vertical Jump', 'Acceleration'
    ]
  },
  mental: {
    label: 'Mental Skills',
    suggestions: [
      'Focus', 'Confidence', 'Composure', 'Determination',
      'Work Ethic', 'Coachability', 'Competitive Spirit',
      'Stress Management', 'Goal Setting', 'Resilience'
    ]
  }
};

const SkillsForm = ({ data, onChange }) => {
  const { showInfo } = useNotification();
  const [activeCategory, setActiveCategory] = useState('technical');
  const [newSkill, setNewSkill] = useState('');
  const [selectedLevel, setSelectedLevel] = useState(3);

  // Ensure data exists
  if (!data) {
    return <div>Loading form...</div>;
  }

  // Add a new skill
  const handleAddSkill = () => {
    if (!newSkill.trim()) {
      showInfo('Please enter a skill name');
      return;
    }

    const updatedData = { ...data };
    if (!updatedData[activeCategory]) {
      updatedData[activeCategory] = [];
    }

    // Check if skill already exists
    const exists = updatedData[activeCategory].some(
      skill => skill.name.toLowerCase() === newSkill.toLowerCase()
    );

    if (exists) {
      showInfo('This skill already exists');
      return;
    }

    // Add the skill
    updatedData[activeCategory].push({
      id: Date.now().toString(),
      name: newSkill,
      level: selectedLevel,
      endorsements: []
    });

    onChange(updatedData);
    setNewSkill('');
    setSelectedLevel(3);
  };

  // Remove a skill
  const handleRemoveSkill = (category, skillId) => {
    const updatedData = { ...data };
    
    // Ensure the category exists
    if (!updatedData[category]) {
      return;
    }
    
    updatedData[category] = updatedData[category].filter(skill => skill.id !== skillId);
    onChange(updatedData);
  };

  // Update skill level
  const handleUpdateLevel = (category, skillId, newLevel) => {
    const updatedData = { ...data };
    
    // Ensure the category exists
    if (!updatedData[category]) {
      updatedData[category] = [];
    }
    
    const skill = updatedData[category].find(s => s.id === skillId);
    if (skill) {
      skill.level = newLevel;
      onChange(updatedData);
    }
  };

  // Add skill from suggestion
  const handleAddSuggestion = (skillName) => {
    setNewSkill(skillName);
  };

  // Render skill level stars
  const renderLevelStars = (level, skillId, category) => {
    // For new skill input, handle differently
    if (skillId === 'new') {
      return (
        <div className={styles.levelStars}>
          {[1, 2, 3, 4, 5].map(star => (
            <button
              key={star}
              type="button"
              className={styles.starButton}
              onClick={() => setSelectedLevel(star)}
            >
              {star <= level ? <FaStar /> : <FaRegStar />}
            </button>
          ))}
        </div>
      );
    }
    
    // For existing skills
    return (
      <div className={styles.levelStars}>
        {[1, 2, 3, 4, 5].map(star => (
          <button
            key={star}
            type="button"
            className={styles.starButton}
            onClick={() => handleUpdateLevel(category, skillId, star)}
          >
            {star <= level ? <FaStar /> : <FaRegStar />}
          </button>
        ))}
      </div>
    );
  };

  return (
    <div className={styles.formSection}>
      <h2 className={styles.sectionTitle}>Skills & Abilities</h2>
      
      <p className={styles.description}>
        Add your key skills and rate your proficiency level. These help clubs understand your strengths.
      </p>

      {/* Category Tabs */}
      <div className={styles.categoryTabs}>
        {Object.entries(SKILL_CATEGORIES).map(([key, category]) => (
          <button
            key={key}
            type="button"
            className={`${styles.categoryTab} ${activeCategory === key ? styles.active : ''}`}
            onClick={() => setActiveCategory(key)}
          >
            {category.label}
            {data[key]?.length > 0 && (
              <span className={styles.skillCount}>{data[key].length}</span>
            )}
          </button>
        ))}
      </div>

      {/* Add New Skill */}
      <div className={styles.addSkillSection}>
        <div className={styles.addSkillForm}>
          <input
            type="text"
            className={styles.input}
            value={newSkill}
            onChange={(e) => setNewSkill(e.target.value)}
            placeholder={`Add a ${activeCategory} skill...`}
            onKeyPress={(e) => e.key === 'Enter' && handleAddSkill()}
          />
          
          <div className={styles.levelSelector}>
            <span className={styles.levelLabel}>Level:</span>
            {renderLevelStars(selectedLevel, 'new', null)}
          </div>
          
          <button
            type="button"
            className={styles.addButton}
            onClick={handleAddSkill}
          >
            <FaPlus /> Add Skill
          </button>
        </div>

        {/* Skill Suggestions */}
        <div className={styles.suggestions}>
          <p className={styles.suggestionsLabel}>Suggestions:</p>
          <div className={styles.suggestionTags}>
            {SKILL_CATEGORIES[activeCategory].suggestions.map(suggestion => {
              const alreadyAdded = data[activeCategory]?.some(
                skill => skill.name.toLowerCase() === suggestion.toLowerCase()
              );
              
              if (alreadyAdded) return null;
              
              return (
                <button
                  key={suggestion}
                  type="button"
                  className={styles.suggestionTag}
                  onClick={() => handleAddSuggestion(suggestion)}
                >
                  {suggestion}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Current Skills */}
      <div className={styles.skillsList}>
        <h3 className={styles.subsectionTitle}>
          {SKILL_CATEGORIES[activeCategory].label}
        </h3>
        
        {data[activeCategory]?.length > 0 ? (
          <div className={styles.skills}>
            {data[activeCategory].map(skill => (
              <div key={skill.id} className={styles.skillItem}>
                <div className={styles.skillHeader}>
                  <span className={styles.skillName}>{skill.name}</span>
                  <button
                    type="button"
                    className={styles.removeButton}
                    onClick={() => handleRemoveSkill(activeCategory, skill.id)}
                  >
                    <FaTrash />
                  </button>
                </div>
                
                <div className={styles.skillDetails}>
                  {renderLevelStars(skill.level, skill.id, activeCategory)}
                  
                  {skill.endorsements?.length > 0 && (
                    <span className={styles.endorsementCount}>
                      {skill.endorsements.length} endorsements
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className={styles.emptyState}>
            <p>No {activeCategory} skills added yet.</p>
            <p>Start by adding skills from the suggestions above or create your own.</p>
          </div>
        )}
      </div>

      {/* Skill Level Guide */}
      <div className={styles.levelGuide}>
        <h4>Skill Level Guide:</h4>
        <div className={styles.levelDescriptions}>
          <div><FaStar /> Beginner - Just starting to learn</div>
          <div><FaStar /><FaStar /> Developing - Some experience</div>
          <div><FaStar /><FaStar /><FaStar /> Proficient - Comfortable and consistent</div>
          <div><FaStar /><FaStar /><FaStar /><FaStar /> Advanced - High level of expertise</div>
          <div><FaStar /><FaStar /><FaStar /><FaStar /><FaStar /> Expert - Elite level performance</div>
        </div>
      </div>
    </div>
  );
};

export default SkillsForm;
